import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, throwError, EMPTY, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@services';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { CookieService } from 'ngx-cookie-service';

const EXPIRED_TOKEN = 'expired_token';
const CANT_VALIDATE_TOKEN = "Can't validate access token";

/**
 * Error Interceptor Service
 * Push an error into error stream service
 */
@Injectable()
export class ErrorInterceptorService implements HttpInterceptor {

    isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private http: HttpClient;

    constructor(
        private authService: AuthService,
        private router: Router,
        private cookieService: CookieService,
        private httpBackend: HttpBackend
    ) {
        // HttpClient construido sobre HttpBackend para que el request de refresh
        // no pase por ningún interceptor y no genere un loop
        this.http = new HttpClient(httpBackend);
    }

    private addToken(request: HttpRequest<any>, token: string) {
        return request.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
        });
    }

    private isExpiredTokenError(err: any): boolean {
        return err.error === EXPIRED_TOKEN
            || err.error?.message === EXPIRED_TOKEN
            || err.error?.error === EXPIRED_TOKEN
            || err.error === CANT_VALIDATE_TOKEN
            || err.error?.message === CANT_VALIDATE_TOKEN;
    }

    private handleExpiredToken(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const refreshToken = this.cookieService.get(environment.refreshTokenName);
            const authUrl = `${environment.envVar.AUTHENTICATION_API_URL}/api/idp/token`;

            return this.http.post<any>(authUrl, {
                refresh_token: refreshToken,
            }).pipe(
                switchMap((auth: any) => {
                    this.isRefreshing = false;
                    this.authService.createCookies(auth);
                    this.refreshTokenSubject.next(auth.access_token);
                    return next.handle(this.addToken(request, auth.access_token));
                }),
                catchError((err) => {
                    this.isRefreshing = false;
                    this.authService.logout(true);
                    return throwError(err);
                })
            );
        } else {
            // Otro request ya disparó el refresh — esperar al token nuevo
            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => next.handle(this.addToken(request, token)))
            );
        }
    }

    /**
     * Intercept logic
     * @param request
     * @param next
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError(err => {
                if (err.status === 403 && this.isExpiredTokenError(err) && this.cookieService.get(environment.refreshTokenName)) {
                    return this.handleExpiredToken(request, next);
                }

                if (err.status === 401) {
                    if (this.cookieService.get(environment.tokenName)) {
                        this.cookieService.delete(environment.tokenName);
                    }
                    // Perfil público (/profile/:slug): es una vista sin sesión; algunas llamadas
                    // pueden responder 401 para un visitante anónimo y no deben expulsarlo a /login.
                    if (!this.router.url.includes('/profile/')) {
                        this.router.navigate(['login']);
                    }
                    return throwError(err);
                }

                if (err.status === 503) {
                    this.router.navigate(['maintenance']);
                    return EMPTY;
                }

                return throwError(err);
            })
        );
    }
}
